import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Input, Button, message as antdMessage } from "antd";
import {
  KeyOutlined,
  LockOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons";
import m_logo from "../logo.png";
import AdminPortal from "./AdminPortal";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const roleRedirects = {
  SUPER_ADMIN: "/super-admin/new-dashboard",
  DMA_DESK_OFFICER: "/super-admin/new-dashboard",
  DIVISIONAL_OFFICER: "/super-admin/new-dashboard",
  DISTRICT_ADMIN: "/super-admin/new-dashboard",
  ULB: "/super-admin/new-dashboard",
  ULB_CHIEF_OFFICIER: "/super-admin/new-dashboard",
  DMA_DESK_USER: "/super-admin/new-dashboard",
  CADER: "/super-admin/form-collection",
  visitor: "/super-admin/new-dashboard",
  Visitor: "/super-admin/new-dashboard",
  VISITOR: "/super-admin/new-dashboard",
  DMA_ADMIN: "/super-admin/new-dashboard",
  "DMA Admin": "/super-admin/new-dashboard",
};

const roleMapping = {
  SUPER_ADMIN: "ADMIN",
  DMA_DESK_OFFICER: "DMA",
  DIVISIONAL_OFFICER: "RDMA",
  DISTRICT_ADMIN: "DAO",
  ULB: "ULB",
  ULB_OFFICER: "ULB",
  DMA_DESK_USER: "DMA_USER",
  CADER: "CADER",
  visitor: "visitor",
  Visitor: "visitor",
  VISITOR: "visitor",
  DMA_ADMIN: "DMA Admin",
};

const saveUserSession = (responseData, username) => {
  const savedTheme = sessionStorage.getItem("loginTheme");
  const userRole = responseData.role_name;
  const workflowRole = roleMapping[userRole] || userRole;
  const resolvedUser = responseData.user || responseData || {};

  sessionStorage.clear();

  if (savedTheme) {
    sessionStorage.setItem("loginTheme", savedTheme);
  }

  sessionStorage.setItem("token", responseData.token || "");
  sessionStorage.setItem("user", JSON.stringify(resolvedUser));
  sessionStorage.setItem("user_id", String(resolvedUser.id || responseData.id || ""));
  sessionStorage.setItem("userRole", workflowRole || "");
  sessionStorage.setItem("userName", resolvedUser.username || username || "");
  sessionStorage.setItem("district_id", String(responseData.district_id || ""));
  sessionStorage.setItem("division_id", String(responseData.division_id || ""));
  sessionStorage.setItem("ulb_id", String(responseData.ulb_id || ""));
  sessionStorage.setItem("desk_id", String(responseData.desk_id || ""));

  if (Array.isArray(responseData.desk_ids)) {
    sessionStorage.setItem("desk_ids", JSON.stringify(responseData.desk_ids));
  } else {
    sessionStorage.setItem("desk_ids", responseData.desk_ids || "");
  }

  sessionStorage.setItem(
    "desk_assignments",
    JSON.stringify(responseData.desk_assignments || [])
  );

  sessionStorage.setItem(
    "has_form_builder_access",
    responseData.has_form_builder_access ? "true" : "false"
  );
};

const fontAwesomeCDNId = "font-awesome-cdn-login";
if (!document.getElementById(fontAwesomeCDNId)) {
  const fontAwesomeCDN = document.createElement("link");
  fontAwesomeCDN.id = fontAwesomeCDNId;
  fontAwesomeCDN.rel = "stylesheet";
  fontAwesomeCDN.href =
    "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css";
  document.head.appendChild(fontAwesomeCDN);
}

const loginStyleSheetId = "login-form-inline-styles";
if (!document.getElementById(loginStyleSheetId)) {
  const styleSheet = document.createElement("style");
  styleSheet.id = loginStyleSheetId;
  styleSheet.innerText = `
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    @media (max-width: 700px) {
      .login-container-responsive {
        flex-direction: column;
      }
    }
  `;
  document.head.appendChild(styleSheet);
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #ffffff 0%, #a494ee70 100%)",
    padding: "20px",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  container: {
    display: "flex",
    width: "100%",
    maxWidth: "1100px",
    borderRadius: "24px",
    overflow: "hidden",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
    flexWrap: "wrap",
  },
  imageSection: {
    flex: "1",
    background: "linear-gradient(145deg, #0183f5, #060606)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "30px",
    minWidth: "280px",
  },
  imageContent: {
    textAlign: "center",
    color: "white",
    width: "100%",
  },
  welcomeBadge: {
    background: "rgba(255, 255, 255, 0.15)",
    padding: "6px 20px",
    borderRadius: "50px",
    display: "inline-block",
    marginBottom: "20px",
    fontSize: "13px",
    fontWeight: "600",
    letterSpacing: "1px",
  },
  welcomeTitle: {
    fontSize: "28px",
    fontWeight: "800",
    marginBottom: "15px",
    lineHeight: "1.2",
  },
  welcomeSubtitle: {
    fontSize: "14px",
    marginBottom: "25px",
    opacity: "0.95",
    lineHeight: "1.5",
  },
  logoContainer: {
    marginBottom: "25px",
  },
  govtBadge: {
    background: "rgba(255, 255, 255, 0.1)",
    padding: "12px",
    borderRadius: "12px",
  },
  govtTitle: {
    fontSize: "20px",
    marginBottom: "4px",
    fontWeight: "500",
  },
  govtSubtitle: {
    fontSize: "20px",
    fontWeight: "800",
  },
  formSection: {
    flex: "1",
    background: "white",
    padding: "35px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    minWidth: "300px",
  },
  formHeader: {
    marginBottom: "20px",
  },
  formTitle: {
    fontSize: "28px",
    marginBottom: "8px",
    marginTop: 0,
    fontWeight: "bold",
    color: "#0d47a1",
  },
  formSubtitle: {
    color: "#546e7a",
    fontSize: "14px",
    margin: 0,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    color: "#37474f",
    fontWeight: "600",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  labelIcon: {
    width: "18px",
    color: "#0d47a1",
    fontSize: "14px",
  },
  inputContainer: {
    position: "relative",
    width: "100%",
  },
  input: (hasError, hasIcon = false, disabled = false) => ({
    width: "100%",
    padding: `12px ${hasIcon ? "40px" : "14px"} 12px 14px`,
    borderRadius: "10px",
    border: `1px solid ${hasError ? "#d32f2f" : "#b0bec5"}`,
    fontSize: "15px",
    boxSizing: "border-box",
    background: disabled ? "#f5f5f5" : "#fff",
    outline: "none",
    transition: "all 0.3s ease",
  }),
  passwordToggle: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#0d47a1",
    fontSize: "16px",
    padding: "5px",
  },
  help: {
    color: "#d32f2f",
    fontSize: "11px",
    marginTop: "3px",
  },
  buttonGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginTop: "15px",
  },
  loginButton: {
    padding: "14px",
    background: "linear-gradient(135deg, #0d47a1 0%, #1976d2 100%)",
    color: "white",
    border: "none",
    borderRadius: "10px",
    fontSize: "16px",
    fontWeight: "700",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
  },
  registerButton: {
    flex: 1,
    padding: "12px",
    background: "white",
    color: "#0d47a1",
    border: "2px solid #0d47a1",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  doubleButtonRow: {
    display: "flex",
    gap: "15px",
    marginTop: "5px",
  },
  alert: (isSuccess) => ({
    padding: "10px 14px",
    borderRadius: "8px",
    marginBottom: "15px",
    fontWeight: "500",
    background: isSuccess ? "#e8f5e9" : "#ffebee",
    color: isSuccess ? "#1b5e20" : "#c62828",
    border: `1px solid ${isSuccess ? "#c8e6c9" : "#ffcdd2"}`,
    fontSize: "13px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  }),
  divider: {
    display: "flex",
    alignItems: "center",
    textAlign: "center",
    margin: "10px 0 8px",
    color: "#6c757d",
    fontSize: "13px",
  },
  dividerLine: {
    flex: 1,
    height: "1px",
    background: "#e0e0e0",
  },
  dividerText: {
    padding: "0 12px",
  },
  loadingSpinner: {
    display: "inline-block",
    width: "14px",
    height: "14px",
    border: "2px solid rgba(255,255,255,0.3)",
    borderRadius: "50%",
    borderTopColor: "#fff",
    animation: "spin 1s ease-in-out infinite",
  },
};

const changePasswordStyles = {
  modal: { zIndex: 2000 },
  label: {
    display: "block",
    marginBottom: "8px",
    fontWeight: 500,
    fontSize: "15px",
  },
  requiredStar: { color: "#e53e3e" },
  passwordHint: {
    marginTop: "4px",
    fontSize: "11px",
    color: "#718096",
  },
  errorText: {
    marginTop: "4px",
    fontSize: "12px",
    color: "#e53e3e",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
};

// Error Modal Component
const ErrorModal = ({ visible, message, onClose }) => {
  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <ExclamationCircleOutlined style={{ color: "#e53e3e", fontSize: "20px" }} />
          <span style={{ color: "#e53e3e" }}>Login Failed</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button
          key="close"
          type="primary"
          onClick={onClose}
          style={{
            borderRadius: "8px",
            background: "linear-gradient(135deg, #0d47a1, #1976d2)",
            border: "none",
          }}
        >
          Try Again
        </Button>
      ]}
      width={400}
      centered
      closable={true}
    >
      <div style={{ padding: "16px 0", textAlign: "center" }}>
        <div style={{ marginBottom: "16px" }}>
          <ExclamationCircleOutlined style={{ color: "#e53e3e", fontSize: "48px" }} />
        </div>
        <p style={{ fontSize: "16px", color: "#37474f", marginBottom: "8px" }}>
          {message || "Invalid username or password. Please try again."}
        </p>
        <p style={{ fontSize: "13px", color: "#718096", marginTop: "8px" }}>
          Please check your credentials and try again.
        </p>
      </div>
    </Modal>
  );
};

const LoginForm = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(false);

  const [adminPortalVisible, setAdminPortalVisible] = useState(false);
  const [theme, setTheme] = useState(null);
  const [themeLoaded, setThemeLoaded] = useState(false);

  const [isChangePasswordModalVisible, setIsChangePasswordModalVisible] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [pendingUserData, setPendingUserData] = useState(null);

  const shouldSkipPasswordChange = (roleName) => {
    const skipRoles = ["VISITOR", "CADER", "visitor", "cader", "Visitor", "Cader"];
    return skipRoles.includes(roleName);
  };

  const validatePassword = (password) => {
    if (!password) return "Password is required";
    if (password.length < 6) return "Minimum 6 characters required";
    return "";
  };

  const closeChangePasswordModal = () => {
    setIsChangePasswordModalVisible(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
    setPasswordError("");
    setConfirmPasswordError("");
    setPendingUserData(null);
  };

  const closeErrorModal = () => {
    setShowErrorModal(false);
    setErrorModalMessage("");
    setPassword(""); // Clear only password
  };

  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    setPasswordError(validatePassword(value));

    if (confirmNewPassword && value !== confirmNewPassword) {
      setConfirmPasswordError("Passwords do not match");
    } else if (confirmNewPassword && value === confirmNewPassword) {
      setConfirmPasswordError("");
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmNewPassword(value);
    setConfirmPasswordError(value && value !== newPassword ? "Passwords do not match" : "");
  };

  const loadThemeFromServer = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/theme`);
      const data = await response.json();
      if (data.success && data.theme) {
        setTheme(data.theme);
        sessionStorage.setItem("loginTheme", JSON.stringify(data.theme));
      } else {
        const savedTheme = sessionStorage.getItem("loginTheme");
        if (savedTheme) {
          setTheme(JSON.parse(savedTheme));
        }
      }
    } catch (error) {
      console.error("Error loading theme from server:", error);
      const savedTheme = sessionStorage.getItem("loginTheme");
      if (savedTheme) {
        try {
          setTheme(JSON.parse(savedTheme));
        } catch {
          setTheme(null);
        }
      }
    } finally {
      setThemeLoaded(true);
    }
  };

  const saveThemeToServer = async (newTheme) => {
    try {
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/admin/theme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ theme: newTheme })
      });

      const data = await response.json();

      if (data.success) {
        setTheme(newTheme);
        sessionStorage.setItem("loginTheme", JSON.stringify(newTheme));
        antdMessage.success("Theme saved for all users!");
        return true;
      } else {
        antdMessage.error("Failed to save theme");
        return false;
      }
    } catch (error) {
      console.error("Error saving theme to server:", error);
      antdMessage.error("Failed to save theme to server");
      return false;
    }
  };

  const handleKeyPress = useCallback((e) => {
    if (e.ctrlKey && e.shiftKey && e.key === "A") {
      setAdminPortalVisible(true);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [handleKeyPress]);

  useEffect(() => {
    loadThemeFromServer();
  }, []);

  const getDynamicStyles = () => {
    if (!theme) return styles;

    const pageBg =
      theme.pageBg?.type === "gradient"
        ? `linear-gradient(135deg, ${theme.pageBg.gradientStart} 0%, ${theme.pageBg.gradientEnd} 100%)`
        : theme.pageBg?.solidColor || styles.page.background;

    const imageSectionBg =
      theme.imageSectionBg?.type === "gradient"
        ? `linear-gradient(145deg, ${theme.imageSectionBg.gradientStart}, ${theme.imageSectionBg.gradientEnd})`
        : theme.imageSectionBg?.solidColor || styles.imageSection.background;

    const loginButtonBg =
      theme.loginButtonBg?.type === "gradient"
        ? `linear-gradient(135deg, ${theme.loginButtonBg.gradientStart} 0%, ${theme.loginButtonBg.gradientEnd} 100%)`
        : theme.loginButtonBg?.solidColor || styles.loginButton.background;

    return {
      ...styles,
      page: {
        ...styles.page,
        background: pageBg,
      },
      imageSection: {
        ...styles.imageSection,
        background: imageSectionBg,
      },
      loginButton: {
        ...styles.loginButton,
        background: loginButtonBg,
      },
      formTitle: {
        ...styles.formTitle,
        color: theme.formTitleColor || styles.formTitle.color,
      },
      welcomeBadge: {
        ...styles.welcomeBadge,
        background: "rgba(255, 255, 255, 0.15)",
      },
      welcomeTitle: {
        ...styles.welcomeTitle,
        color: theme.welcomeTitleColor || styles.welcomeTitle.color,
      },
      welcomeSubtitle: {
        ...styles.welcomeSubtitle,
        color: theme.welcomeSubtitleColor || styles.welcomeSubtitle.color,
      },
      registerButton: {
        ...styles.registerButton,
        background: theme.registerButtonBg || styles.registerButton.background,
        color: theme.registerButtonTextColor || styles.registerButton.color,
        borderColor: theme.registerButtonBorderColor || styles.registerButton.borderColor,
      },
    };
  };

  const dynamicStyles = getDynamicStyles();

  const changePassword = async () => {
    if (!currentPassword) {
      antdMessage.error("Please enter your current password");
      return;
    }
    if (!newPassword) {
      antdMessage.error("Please enter a new password");
      return;
    }
    if (!confirmNewPassword) {
      antdMessage.error("Please confirm your new password");
      return;
    }
    if (passwordError) {
      antdMessage.error(passwordError);
      return;
    }
    if (newPassword !== confirmNewPassword) {
      antdMessage.error("Passwords do not match");
      return;
    }

    setIsChangingPassword(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/login/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: pendingUserData.username,
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmNewPassword,
        })
      });

      const data = await response.json();

      if (data.success) {
        antdMessage.success("Password changed successfully! Please login again.");
        closeChangePasswordModal();
        setUsername("");
        setPassword("");
        setMessage("");

        setTimeout(() => {
          navigate("/", { replace: true });
        }, 2000);
      } else {
        antdMessage.error(data.error || "Failed to change password");
      }
    } catch (error) {
      console.error("Error changing password:", error);
      antdMessage.error("Failed to change password");
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!username || !password) {
      antdMessage.error("Please enter username and password");
      return;
    }
    
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/login/direct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username,
          password: password,
        })
      });

      const data = await response.json();
      console.log("Login response:", data);

      if (!response.ok) {
        throw new Error(data.error || "Invalid username or password");
      }

      const userRole = data.role_name;
      const skipPasswordChange = shouldSkipPasswordChange(userRole);

      if (data.must_change_password === true && !skipPasswordChange) {
        setPendingUserData({
          username: username,
          email: data.email,
          role_name: data.role_name,
          role_id: data.role_id,
          district_id: data.district_id,
          division_id: data.division_id,
          ulb_id: data.ulb_id,
          has_form_builder_access: data.has_form_builder_access,
          desk_assignments: data.desk_assignments,
          desk_ids: data.desk_ids,
        });
        setIsChangePasswordModalVisible(true);
        setLoading(false);
        return;
      }

      saveUserSession(data, username);

      const redirectPath = roleRedirects[userRole] || "/dashboard";

      setMessageType(true);
      setMessage("Login successful! Redirecting...");

      setTimeout(() => {
        navigate(redirectPath, { replace: true });
      }, 1000);
    } catch (error) {
      console.error("Login error:", error);

      let errorMessage = error.message || "Invalid username or password";

      // Show error modal - NO PAGE REFRESH
      setErrorModalMessage(errorMessage);
      setShowErrorModal(true);
      setMessageType(false);
      setMessage("");
    } finally {
      setLoading(false);
    }
  };

  const handleVisitorClick = () => {
    navigate("/visitor");
  };

  const handleThemeSave = async (newTheme) => {
    await saveThemeToServer(newTheme);
  };

  if (!themeLoaded) {
    return (
      <div style={styles.page}>
        <div style={{ textAlign: "center" }}>
          <div style={styles.loadingSpinner}></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={dynamicStyles.page}>
      <div className="login-container-responsive" style={dynamicStyles.container}>
        <div style={dynamicStyles.imageSection}>
          <div style={styles.imageContent}>
            <div style={dynamicStyles.welcomeBadge}>
              {theme?.welcomeBadgeText || "DMA Repository PORTAL"}
            </div>
            <div style={dynamicStyles.welcomeTitle}>
              {theme?.welcomeTitleText || "Welcome to"}
            </div>
            <div style={dynamicStyles.welcomeTitle}>
              {theme?.welcomeTitleSecondText || "DMA Repository Portal"}
            </div>
            <div style={dynamicStyles.welcomeSubtitle}>
              {theme?.welcomeSubtitleText || "Secure access to municipal administration system"}
            </div>
            <div style={styles.logoContainer}>
              {theme?.logoUrl ? (
                <img src={theme.logoUrl} alt="Logo" style={{ width: theme.logoWidth || "100px" }} />
              ) : (
                <img src={m_logo} alt="Logo" style={{ width: theme?.logoWidth || "100px" }} />
              )}
            </div>
            <div style={styles.govtBadge}>
              <div style={styles.govtTitle}>
                {theme?.govtTitleText || "Government Of Maharashtra"}
              </div>
              <div style={styles.govtSubtitle}>
                {theme?.govtSubtitleText || "Directorate of Municipal Administration"}
              </div>
            </div>
          </div>
        </div>

        <div style={styles.formSection}>
          <div style={styles.formHeader}>
            <h1 style={dynamicStyles.formTitle}>{theme?.formTitleText || "Login"}</h1>
            <p style={styles.formSubtitle}>
              {theme?.formSubtitleText || "Sign in with your credentials"}
            </p>
          </div>

          {message && messageType && <div style={styles.alert(messageType)}>{message}</div>}

          <form onSubmit={handleLogin} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>
                <i className="fas fa-user" style={styles.labelIcon}></i>
                Username
              </label>
              <div style={styles.inputContainer}>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  style={styles.input(false, false, false)}
                  required
                />
              </div>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>
                <i className="fas fa-lock" style={styles.labelIcon}></i>
                Password
              </label>
              <div style={styles.inputContainer}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  style={styles.input(false, true, false)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={styles.passwordToggle}
                >
                  <i className={showPassword ? "fas fa-eye-slash" : "fas fa-eye"}></i>
                </button>
              </div>
            </div>

            <div style={styles.buttonGroup}>
              <button type="submit" disabled={loading} style={dynamicStyles.loginButton}>
                {loading ? (
                  <>
                    <span style={styles.loadingSpinner}></span> Verifying...
                  </>
                ) : (
                  <>
                    <i className="fas fa-sign-in-alt"></i> {theme?.loginButtonText || "LOGIN"}
                  </>
                )}
              </button>

              <div style={styles.divider}>
                <span style={styles.dividerLine}></span>
                <span style={styles.dividerText}>New User?</span>
                <span style={styles.dividerLine}></span>
              </div>

              <div style={styles.doubleButtonRow}>
                {theme?.showVisitorButton !== false && (
                  <button
                    type="button"
                    onClick={handleVisitorClick}
                    style={dynamicStyles.registerButton}
                  >
                    <i className="fas fa-user-friends"></i>{" "}
                    {theme?.visitorButtonText || "Visitor Registration"}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>

      <ErrorModal
        visible={showErrorModal}
        message={errorModalMessage}
        onClose={closeErrorModal}
      />

      <Modal
        title="Change Password (First Time Login)"
        open={isChangePasswordModalVisible}
        onCancel={closeChangePasswordModal}
        footer={null}
        width={450}
        centered
        destroyOnClose={true}
        maskClosable={false}
      >
        <div style={{ padding: "16px 0" }}>
          <div style={{ marginBottom: "20px" }}>
            <label style={changePasswordStyles.label}>
              Current Password <span style={changePasswordStyles.requiredStar}>*</span>
            </label>
            <Input.Password
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
              size="large"
              prefix={<LockOutlined style={{ color: "#718096" }} />}
              style={{ borderRadius: "8px" }}
            />
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={changePasswordStyles.label}>
              New Password <span style={changePasswordStyles.requiredStar}>*</span>
            </label>
            <Input.Password
              value={newPassword}
              onChange={handleNewPasswordChange}
              placeholder="Enter new password"
              size="large"
              prefix={<LockOutlined style={{ color: "#718096" }} />}
              style={{
                borderRadius: "8px",
                borderColor: passwordError ? "#e53e3e" : "#d1d5db",
              }}
            />
            {passwordError && (
              <div style={changePasswordStyles.errorText}>
                <ExclamationCircleOutlined />
                {passwordError}
              </div>
            )}
            <div style={changePasswordStyles.passwordHint}>
              Password must be at least 6 characters long
            </div>
          </div>

          <div style={{ marginBottom: "24px" }}>
            <label style={changePasswordStyles.label}>
              Confirm New Password <span style={changePasswordStyles.requiredStar}>*</span>
            </label>
            <Input.Password
              value={confirmNewPassword}
              onChange={handleConfirmPasswordChange}
              placeholder="Confirm new password"
              size="large"
              prefix={<LockOutlined style={{ color: "#718096" }} />}
              style={{
                borderRadius: "8px",
                borderColor: confirmPasswordError ? "#e53e3e" : "#d1d5db",
              }}
            />
            {confirmPasswordError && (
              <div style={changePasswordStyles.errorText}>
                <ExclamationCircleOutlined />
                {confirmPasswordError}
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
            <Button onClick={closeChangePasswordModal} size="large">
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={changePassword}
              loading={isChangingPassword}
              disabled={
                !currentPassword ||
                !newPassword ||
                !confirmNewPassword ||
                passwordError ||
                confirmPasswordError
              }
              size="large"
            >
              Change Password
            </Button>
          </div>
        </div>
      </Modal>

      <AdminPortal
        visible={adminPortalVisible}
        onClose={() => setAdminPortalVisible(false)}
        onThemeSave={handleThemeSave}
        currentTheme={theme}
      />
    </div>
  );
};

export default LoginForm;